import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface AppError {
  id: string;
  title: string;
  message: string;
  type: 'error' | 'warning' | 'info' | 'success';
  timestamp: Date;
  dismissible?: boolean;
  retryable?: boolean;
  retryCallback?: () => void;
  code?: string;
}

export interface FieldValidationError {
  field: string;
  message: string;
  code: string;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  public errors$ = new BehaviorSubject<AppError[]>([]);
  readonly errors = this.errors$.asObservable();

  public fieldErrors$ = new BehaviorSubject<FieldValidationError[]>([]);
  readonly fieldErrors = this.fieldErrors$.asObservable();

  public retrySubject = new Subject<string>();
  readonly retry$ = this.retrySubject.asObservable();

  // Friendly error messages mapping
  public readonly friendlyMessages: { [key: string]: string } = {
    NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
    TIMEOUT: 'The request took too long. Please try again.',
    UNAUTHORIZED: 'You do not have permission to perform this action.',
    NOT_FOUND: 'The requested resource was not found.',
    VALIDATION_ERROR: 'Please check your input and try again.',
    SERVER_ERROR: 'Something went wrong on our end. Please try again later.',
    INVALID_EMAIL: 'Please enter a valid email address.',
    PASSWORD_TOO_SHORT: 'Password must be at least 8 characters long.',
    PASSWORDS_DONT_MATCH: 'Passwords do not match.',
    USERNAME_TAKEN: 'This username is already in use.',
    FILE_TOO_LARGE: 'The file is too large. Maximum size is 5MB.',
    UNSUPPORTED_FILE_TYPE: 'This file type is not supported.',
    REQUIRED_FIELD: 'This field is required.',
    INVALID_FORMAT: 'The format is invalid.',
  };

  constructor() {}

  /**
   * Add an error notification
   * @param title - Error title
   * @param message - Error message (can be error code, will be translated to friendly message)
   * @param type - Error type
   * @param retryCallback - Optional callback for retry action
   * @param errorCode - Optional error code for mapping to friendly messages
   */
  addError(
    title: string,
    message: string,
    type: 'error' | 'warning' | 'info' | 'success' = 'error',
    retryCallback?: () => void,
    errorCode?: string,
  ): void {
    const friendlyMessage = this.getFriendlyMessage(message, errorCode);

    const error: AppError = {
      id: Date.now().toString() + Math.random(),
      title,
      message: friendlyMessage,
      type,
      timestamp: new Date(),
      dismissible: true,
      retryable: !!retryCallback,
      retryCallback,
      code: errorCode,
    };

    const currentErrors = this.errors$.value;
    this.errors$.next([...currentErrors, error]);

    // Auto-dismiss non-error messages after 5 seconds
    if (type !== 'error') {
      setTimeout(() => this.dismissError(error.id), 5000);
    }
  }

  /**
   * Add field-level validation error
   */
  addFieldError(field: string, message: string, code: string = 'VALIDATION_ERROR'): void {
    const currentErrors = this.fieldErrors$.value;
    const existingIndex = currentErrors.findIndex((e) => e.field === field);

    const friendlyMessage = this.getFriendlyMessage(message, code);

    const fieldError: FieldValidationError = {
      field,
      message: friendlyMessage,
      code,
    };

    if (existingIndex !== -1) {
      currentErrors[existingIndex] = fieldError;
    } else {
      currentErrors.push(fieldError);
    }

    this.fieldErrors$.next([...currentErrors]);
  }

  /**
   * Clear field errors for specific field
   */
  clearFieldError(field: string): void {
    const currentErrors = this.fieldErrors$.value;
    this.fieldErrors$.next(currentErrors.filter((e) => e.field !== field));
  }

  /**
   * Clear all field errors
   */
  clearAllFieldErrors(): void {
    this.fieldErrors$.next([]);
  }

  /**
   * Get field error by field name
   */
  getFieldError(field: string): FieldValidationError | undefined {
    return this.fieldErrors$.value.find((e) => e.field === field);
  }

  /**
   * Check if field has error
   */
  hasFieldError(field: string): boolean {
    return this.fieldErrors$.value.some((e) => e.field === field);
  }

  /**
   * Dismiss specific error
   */
  dismissError(id: string): void {
    const currentErrors = this.errors$.value;
    this.errors$.next(currentErrors.filter((error) => error.id !== id));
  }

  /**
   * Trigger retry for error
   */
  retry(errorId: string): void {
    const error = this.errors$.value.find((e) => e.id === errorId);
    if (error && error.retryCallback) {
      error.retryCallback();
      this.dismissError(errorId);
      this.retrySubject.next(errorId);
    }
  }

  /**
   * Clear all errors
   */
  clearAllErrors(): void {
    this.errors$.next([]);
  }

  /**
   * Get friendly error message from code or message
   */
  public getFriendlyMessage(message: string, code?: string): string {
    // Try code first
    if (code && this.friendlyMessages[code]) {
      return this.friendlyMessages[code];
    }

    // Try message as key
    if (this.friendlyMessages[message]) {
      return this.friendlyMessages[message];
    }

    // Return original message if no mapping found
    return message;
  }

  /**
   * Register custom error message mapping
   */
  registerCustomMessage(code: string, message: string): void {
    this.friendlyMessages[code] = message;
  }

  /**
   * Get all current errors
   */
  getCurrentErrors(): AppError[] {
    return this.errors$.value;
  }

  /**
   * Get all current field errors
   */
  getCurrentFieldErrors(): FieldValidationError[] {
    return this.fieldErrors$.value;
  }
}
